import React, { useState, useEffect } from 'react';
import api from '../services/api';
import ModuleManagement from '../components/Admin/ModuleManagement';
import { 
  BookOpenIcon, 
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const AdminContentPage = () => {
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await api.get('/admin/courses');
        if (res.data?.status === 'success') {
          setCourses(res.data.data.courses);
          if (res.data.data.courses.length > 0) {
            setSelectedCourseId(res.data.data.courses[0].id.toString());
          }
        } else {
          setError('Gagal mengambil data kursus');
        }
      } catch (err) {
        setError('Gagal mengambil data kursus');
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat data kursus...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">Terjadi Kesalahan</h3>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mr-4">
                <DocumentTextIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Content Management</h1>
                <p className="text-gray-600">Kelola konten dan modul kursus</p>
              </div>
            </div>
          </div>
        </div>

        {/* Course Selection */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100 mb-8">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
              <BookOpenIcon className="w-4 h-4 mr-2 text-blue-600" />
              Pilih Kursus
            </label>
            <select
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all duration-200 hover:border-blue-400"
              value={selectedCourseId}
              onChange={e => setSelectedCourseId(e.target.value)}
            >
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.judul}
                </option>
              ))}
            </select>
          </div>

          {courses.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpenIcon className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Tidak Ada Kursus</h3>
              <p className="text-gray-600">Belum ada kursus yang tersedia untuk dikelola.</p>
            </div>
          )}
        </div>

        {/* Module Management */}
        {selectedCourseId && courses.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 md:px-8 py-6 border-b border-gray-100">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mr-3">
                  <DocumentTextIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Module Management</h2>
                  <p className="text-gray-600 text-sm">
                    {courses.find(c => c.id.toString() === selectedCourseId)?.judul}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 md:p-8">
              <ModuleManagement courseId={selectedCourseId} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContentPage;
